import { ChangeLog } from "./components/ChangeLog";
import { EvidenceLegend } from "./components/EvidenceLegend";
import { FrontierFeed } from "./components/FrontierFeed";
import { Header } from "./components/Header";
import { MachineAccess } from "./components/MachineAccess";
import { ModelTable } from "./components/ModelTable";
import { currentClaims } from "./compile";
import type { KnowledgeBase } from "./domain";

const formatVerified = (timestamp: string) =>
  new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "UTC",
  }).format(new Date(timestamp));

export const App = ({ knowledgeBase }: { knowledgeBase: KnowledgeBase }) => {
  const claims = currentClaims(knowledgeBase);
  const changes = [...knowledgeBase.changes].sort((a, b) => b.changedAt.localeCompare(a.changedAt));
  return (
    <div id="top">
      <Header />
      <main>
        <section className="hero" aria-labelledby="page-title">
          <div className="hero-primary">
            <h1 id="page-title">What is true about AI right now?</h1>
            <div className="hero-actions">
              <a className="button button-primary" href="/now.md">Read the current brief <span aria-hidden="true">→</span></a>
              <a className="button button-secondary" href="/api/context">&lt;/&gt; Query the API</a>
            </div>
          </div>
          <p className="hero-description">A continuously verified state of the AI frontier, built for humans and machines.</p>
          <div className="verification-stamp">
            <span>Last verified</span>
            <strong>{formatVerified(knowledgeBase.verifiedThrough)} UTC</strong>
            <a href="#about">Verification policy and methodology <span aria-hidden="true">→</span></a>
          </div>
        </section>

        <div className="main-grid">
          <div className="main-column">
            <FrontierFeed claims={claims} entities={knowledgeBase.entities} />
            <ModelTable claims={claims} entities={knowledgeBase.entities} />
          </div>
          <aside className="side-column">
            <ChangeLog changes={changes} claims={claims} />
            <EvidenceLegend />
            <MachineAccess />
          </aside>
        </div>

        <section className="methodology" id="about" aria-labelledby="about-heading">
          <div>
            <h2 id="about-heading">How we verify</h2>
            <p>We record events as time-bound claims, prefer primary artifacts for release facts, and require independent evidence before treating performance assertions as established.</p>
          </div>
          <div>
            <h2>Correction policy</h2>
            <p>Corrections are appended, never silently erased. Superseded and retracted claims remain addressable in the ledger.</p>
          </div>
          <div>
            <h2>Current boundary</h2>
            <p>This first public state covers five source-verified frontier events. It is a real foundation, not a claim of exhaustive coverage.</p>
          </div>
        </section>
      </main>
      <footer>
        <span>Frontier State</span>
        <span>All times in UTC</span>
        <a href="/feed.xml">RSS</a>
        <a href="/llms.txt">llms.txt</a>
      </footer>
    </div>
  );
};

